import React, { useEffect, useState } from "react";
import API_CONFIG from "../../../lib/ApiConfig";
import { OMIGCard } from "../OMIGScreenWrapper";
import { useAuth } from "../../../hooks/useAuth";
import { formatCurrency } from "../../../lib/utils";
import { LoaderCircle, ChevronDown, ChevronUp, Gift } from "lucide-react";

const formatDate = (dateString) => {
  if (!dateString) return "";
  const d = new Date(dateString);
  return d.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const OMIGPayouts = ({ customer }) => {
  const { getIdToken, user, loading: authLoading } = useAuth();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const parsePayouts = (payouts) => {
      if (!Array.isArray(payouts)) return [];
      return payouts.map((p) => ({ ...p, reveal: false }));
    };

    const load = async () => {
      // Only attempt API call when auth is ready and we have a user
      if (authLoading || !user) {
        return;
      }

      setLoading(true);
      try {
        const token = await getIdToken();
        const uri = `api/payouts`;
        const url = API_CONFIG.getInternalUrl(uri);

        const res = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "x-user-token": token,
          },
        });

        if (res.ok) {
          const data = await res.json();
          const payouts = data || [];
          setItems(parsePayouts(payouts));
        }
      } catch (err) {
        console.error("Error loading payouts:", err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [getIdToken, user, authLoading]);

  const toggleReveal = (payoutId) => {
    setItems((prev) =>
      prev.map((p) =>
        p.id === payoutId
          ? {
              ...p,
              reveal: !p.reveal,
            }
          : p
      )
    );
  };

  if (!loading && (!items || items.length === 0)) {
    return null;
  }

  return (
    <div id="customer-payouts-container" className="space-y-3">
      <div className="text-center my-3">
        <h3 className="text-2xl font-semibold text-veryDarkGrey">Payouts</h3>
        <span className="text-base text-gray-500">
          Payment sent via email from Ownli
        </span>
      </div>

      {loading && (!items || !items?.length) && (
        <div className="flex items-center justify-center py-6">
          <LoaderCircle className="w-6 h-6 text-crimsonGlory animate-spin" />
        </div>
      )}

      {items?.map((payout) => {
        const detailRewards = payout?.rewards || payout?.policyRewards || [];

        return (
          <OMIGCard
            key={payout?.id}
            variant="outline"
            withHoverEffect={false}
            onClick={() => toggleReveal(payout?.id)}
            className="payout-card cursor-pointer !rounded-xl"
          >
            <div className="pb-1">
              <div className="flex items-center gap-4">
                <div className="shrink-0 mr-2">
                  <div className="w-9 h-9 rounded-full bg-crimsonGlory/10 flex items-center justify-center">
                    <Gift className="w-4 h-4 text-crimsonGlory" />
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  {payout?.paymentReferenceLink ? (
                    <a
                      href={payout?.paymentReferenceLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-base font-semibold text-crimsonGlory hover:text-crimsonGlory/80 truncate block"
                    >
                      #{payout?.paymentReferenceId}
                    </a>
                  ) : (
                    <h3 className="text-base font-semibold text-veryDarkGrey truncate">
                      #{payout?.paymentReferenceId}
                    </h3>
                  )}
                  <span className="text-sm text-gray-500">
                    {formatDate(payout?.dateClaimed)}
                  </span>
                </div>

                <div className="shrink-0 text-right">
                  <span className="text-lg font-semibold text-veryDarkGrey">
                    {formatCurrency(payout?.amount, '')}
                  </span>
                </div>
              </div>
            </div>

            {detailRewards?.length > 0 && (
              <div
                className={`transition-[max-height,opacity] duration-200 ease-in-out overflow-hidden ${
                  payout?.reveal ? "max-h-64 opacity-100" : "max-h-0 opacity-0"
                }`}
              >
                <div className="mt-2 border-t border-gray-100 pt-2 space-y-2">
                  {detailRewards?.map((reward) => (
                    <div
                      key={reward?.id}
                      className="flex items-center justify-between text-sm text-veryDarkGrey"
                    >
                      <div className="flex items-center gap-3 mr-2 min-w-0">
                        {reward?.buyer?.logo && (
                          <div className="w-10 h-10 rounded-md flex items-center justify-center overflow-hidden">
                            <img
                              src={reward?.buyer?.logo}
                              alt={
                                reward?.buyer?.name || reward?.buyer?.caption || "Reward logo"
                              }
                              className="max-w-full max-h-full object-contain"
                            />
                          </div>
                        )}
                        <div className="min-w-0">
                          <div className="text-xs text-gray-500 truncate">
                            {reward?.buyer?.name}
                          </div>
                          <div className="font-medium truncate">
                            {reward?.description}
                          </div>
                        </div>
                      </div>
                      <div className="shrink-0 font-semibold text-sm">
                        {formatCurrency(reward?.amount, '')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="pt-1 mt-1 flex justify-center">
              <button
                type="button"
                className="flex items-center gap-1 text-xs text-crimsonGlory hover:text-crimsonGlory/80"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleReveal(payout?.id);
                }}
              >
                {payout?.reveal ? (
                  <>
                    <ChevronUp className="w-4 h-4 mb-0.5" />
                    <span>Hide details</span>
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4 mb-0.5" />
                    <span>View details</span>
                  </>
                )}
              </button>
            </div>
          </OMIGCard>
        );
      })}
    </div>
  );
};

export default OMIGPayouts;
